import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjects } from '../../contexts/ProjectContext';
import { useAuthState } from '../../hooks/useAuthState';
import { aiService } from '../../services/AIService';
import { cn } from '../../utils/cn';
import {
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
  PlayIcon,
  ClockIcon,
  LightBulbIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  actions?: ChatAction[];
}

interface ChatAction {
  label: string;
  icon: 'play' | 'clock' | 'chart';
  href: string;
}

const CHAT_STORAGE_KEY = 'pomomax_chat_history';
const MAX_STORED_MESSAGES = 50;

const stripMarkdown = (text: string) =>
  text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/^#+\s/gm, '').replace(/^-\s/gm, '• ');

// Detect if AI response suggests actionable items
const detectActions = (content: string): ChatAction[] => {
  const actions: ChatAction[] = [];
  const lower = content.toLowerCase();

  if (lower.includes('start a pomodoro') || lower.includes('start a session') || lower.includes('ready to start') || lower.includes('begin a session')) {
    actions.push({ label: 'Start Pomodoro', icon: 'play', href: '/timer' });
  }
  if (lower.includes('check your statistics') || lower.includes('view your stats') || lower.includes('look at your progress')) {
    actions.push({ label: 'View Statistics', icon: 'chart', href: '/statistics' });
  }
  if (lower.includes('take a break') || lower.includes('short break') || lower.includes('rest for')) {
    actions.push({ label: 'Start Break', icon: 'clock', href: '/timer' });
  }

  return actions;
};

export const ProductivityChat: React.FC = () => {
  const { user } = useAuthState();
  const { pomodoros, tasks } = useProjects();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Generate contextual suggestions based on time and data
  const suggestions = useMemo(() => {
    const hour = new Date().getHours();
    const incompleteTasks = tasks.filter(t => !t.completed);
    const todayPomodoros = pomodoros.filter(p => {
      const d = new Date(p.startTime || p.completedAt || 0);
      return d.toDateString() === new Date().toDateString();
    });

    const prompts: { text: string; icon: typeof LightBulbIcon }[] = [];

    if (hour < 12) {
      prompts.push({ text: 'Plan my morning', icon: LightBulbIcon });
    } else if (hour < 17) {
      prompts.push({ text: 'What should I focus on now?', icon: LightBulbIcon });
    } else {
      prompts.push({ text: 'Summarize my day', icon: ChartBarIcon });
    }

    if (incompleteTasks.length > 0) {
      prompts.push({ text: 'Prioritize my tasks', icon: LightBulbIcon });
    }

    if (todayPomodoros.length > 0) {
      prompts.push({ text: `How productive was I today?`, icon: ChartBarIcon });
    } else {
      prompts.push({ text: 'Help me get started', icon: PlayIcon });
    }

    prompts.push({ text: 'Tips to improve focus', icon: SparklesIcon });

    return prompts.slice(0, 4);
  }, [tasks, pomodoros]);

  // Set user ID for AI service
  useEffect(() => {
    if (user) {
      aiService.setUserId(user.id);
      aiService.testConnection();
    }
  }, [user]);

  // Load chat history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CHAT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          setShowSuggestions(false);
        }
      }
    } catch {}
  }, []);

  // Save chat history to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      const toStore = messages.slice(-MAX_STORED_MESSAGES);
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(toStore));
    }
  }, [messages]);

  // Welcome message when opened for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: crypto.randomUUID(),
        role: 'assistant',
        content: "Hi! I'm your productivity assistant. I have access to all your tasks, projects, and pomodoro history. Ask me anything about your productivity or let me help you plan your work.",
        timestamp: Date.now()
      }]);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading || !user) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowSuggestions(false);

    try {
      const context = {
        recentPomodoros: pomodoros.slice(-10),
        incompleteTasks: tasks.filter(t => !t.completed),
        productivityPatterns: await aiService.analyzeProductivityPatterns(pomodoros, tasks),
        currentTime: Date.now()
      };

      const response = await aiService.generateChatResponse(content, context);
      const actions = detectActions(response.content);

      setMessages(prev => [...prev, { ...response, actions }]);
    } catch (error) {
      console.error('Error getting chat response:', error);
      toast.error('Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionClick = (text: string) => {
    sendMessage(text);
  };

  const clearHistory = () => {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setMessages([{
      id: crypto.randomUUID(),
      role: 'assistant',
      content: "Chat cleared! How can I help you?",
      timestamp: Date.now()
    }]);
    setShowSuggestions(true);
  };

  const ActionIcon = ({ icon }: { icon: string }) => {
    switch (icon) {
      case 'play': return <PlayIcon className="h-3.5 w-3.5" />;
      case 'clock': return <ClockIcon className="h-3.5 w-3.5" />;
      case 'chart': return <ChartBarIcon className="h-3.5 w-3.5" />;
      default: return <SparklesIcon className="h-3.5 w-3.5" />;
    }
  };

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 rounded-full h-12 w-12 p-0 shadow-lg z-50"
      >
        <SparklesIcon className="h-5 w-5" />
      </Button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-20 right-4 w-full max-w-md bg-background rounded-2xl shadow-xl border z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <Avatar>
                  <SparklesIcon className="h-5 w-5" />
                </Avatar>
                <div>
                  <h3 className="font-medium">Productivity Assistant</h3>
                  <p className="text-sm text-muted-foreground">Here to help you focus</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearHistory}
                    className="h-8 px-2 text-xs text-muted-foreground"
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 p-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="h-[400px] p-4">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id}>
                    <div
                      className={cn(
                        "flex gap-3 max-w-[85%]",
                        message.role === 'assistant' ? "mr-auto" : "ml-auto flex-row-reverse"
                      )}
                    >
                      {message.role === 'assistant' && (
                        <Avatar className="h-8 w-8 shrink-0">
                          <SparklesIcon className="h-4 w-4" />
                        </Avatar>
                      )}
                      <div
                        className={cn(
                          "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                          message.role === 'assistant'
                            ? "bg-muted"
                            : "bg-primary text-primary-foreground"
                        )}
                      >
                        {stripMarkdown(message.content)}
                      </div>
                    </div>

                    {/* Action buttons */}
                    {message.actions && message.actions.length > 0 && (
                      <div className="flex gap-2 mt-2 ml-11">
                        {message.actions.map((action, i) => (
                          <button
                            key={i}
                            onClick={() => navigate(action.href)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full hover:bg-primary/20 transition-colors"
                          >
                            <ActionIcon icon={action.icon} />
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Suggested prompts */}
                {showSuggestions && messages.length <= 1 && !isLoading && (
                  <div className="space-y-2 mt-2">
                    <p className="text-xs text-muted-foreground ml-11">Try asking:</p>
                    <div className="grid grid-cols-2 gap-2 ml-11">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => handleSuggestionClick(s.text)}
                          className="flex items-center gap-2 px-3 py-2 bg-muted/50 hover:bg-muted rounded-xl text-xs text-left transition-colors"
                        >
                          <s.icon className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span>{s.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isLoading && (
                  <div className="flex gap-3 max-w-[80%]">
                    <Avatar className="h-8 w-8 shrink-0">
                      <SparklesIcon className="h-4 w-4" />
                    </Avatar>
                    <div className="bg-muted rounded-2xl px-4 py-3 flex items-center gap-1.5">
                      <div className="h-2 w-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="h-2 w-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="h-2 w-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t">
              <div className="flex gap-2">
                <Textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask for productivity advice..."
                  className="min-h-[44px] max-h-32 resize-none"
                  rows={1}
                />
                <Button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="h-11 w-11 p-0 shrink-0"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
